import BarcodeTopper from "components/BarcodeTopper"
import Kicker from "components/Kicker"
import { graphql, useStaticQuery } from "gatsby"
import gsap from "gsap"
import { ReactComponent as ArrowSVG } from "images/arrowRight.svg"
import ArrowPartIMG from "images/uvp/ArrowPart.svg"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useAnimation from "library/useAnimation"
import useMedia from "library/useMedia"
import { useRef } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"
import Feature from "./Feature"

export default function YouVeePees() {
	const word = useRef<HTMLDivElement | null>(null)
	const arrow = useRef<HTMLImageElement | null>(null)
	const shouldAnimate = useMedia(true, true, true, false)
	const mobile = useMedia(false, false, false, true)
	const xVal = useMedia(-68, -68, -40, 0)

	useAnimation(() => {
		if (!shouldAnimate) return
		gsap
			.timeline({
				scrollTrigger: {
					trigger: word.current,
					start: "center 110%",
					end: "center 40%",
					scrub: 0.25,
				},
				defaults: { ease: "power2.out", duration: 2 },
			})
			.from(word.current, {
				x: xVal,
			})
			.from(
				arrow.current,
				{
					x: xVal,
				},
				0,
			)
	}, [shouldAnimate, xVal])

	const homeFeaturesQuery: Queries.HomeFeaturesQuery = useStaticQuery(graphql`
		query HomeFeatures {
			allHomeFeaturesJson {
				edges {
					node {
						animatedIcon
						title
						description
						descriptionWidth
						mobileDescriptionWidth
						blueText
						cards {
							image {
								childImageSharp {
									gatsbyImageData(layout: FULL_WIDTH)
								}
							}
							title
							description
							descriptionWidth
							mobileDescriptionWidth
							hasTag
						}
					}
				}
			}
		}
	`)

	const features = homeFeaturesQuery.allHomeFeaturesJson.edges.map(
		(edge) => edge.node,
	)

	return (
		<>
			<Wrapper>
				{mobile ? (
					<StyledKicker barcodes="double" variant="long">
						Save Time and Money
					</StyledKicker>
				) : (
					<Topper>Save Time and Money</Topper>
				)}
				<Title>
					Streamline
					<div style={{ width: "1350px" }}>
						<Arrow aria-hidden>
							<img ref={arrow} src={ArrowPartIMG} alt="" />
						</Arrow>
						<LastWord ref={word}>purchasing</LastWord>
					</div>
				</Title>
				<Bullets>
					<Bullet>
						<BulletArrow />
						Centralize
					</Bullet>
					<Bullet>
						<BulletArrow />
						Control
					</Bullet>
					<Bullet>
						<BulletArrow />
						Optimize
					</Bullet>
				</Bullets>
				<Features>
					{features.map((feature) => (
						<Feature key={feature.title} feature={feature} />
					))}
				</Features>
			</Wrapper>
		</>
	)
}

const Wrapper = styled.div`
	${fresponsive(css`
		position: relative;
		padding: 98px 60px 142px 40px;
	`)}

	${ftablet(css`
		padding: 124px 69px 189px 70px;
	`)}

  ${fmobile(css`
		padding: 72px 0 28px;
	`)}
`

const Topper = styled(BarcodeTopper)`
	${fresponsive(css`
		color: ${colors.grey2};
		border: none;
		padding-bottom: 0;
		margin-bottom: 24px;
		padding-left: 12px;
	`)}

	${ftablet(css`
		padding-left: 70px;
	`)}

	${fmobile(css`
		display: none;
	`)}

	svg {
		${fresponsive(css`
			path {
				fill: ${colors.grey2};
			}
		`)}
	}
`

const Title = styled.div`
	${fresponsive(css`
		${textStyles.h1};
		width: 1105px;
	`)}

	${ftablet(css`
		${textStyles.h2};
		width: 739px;
		padding-left: 61px;
	`)}

  ${fmobile(css`
		${textStyles.h4};
		width: 253px;
		margin: 13px 0 0 20px;
	`)}
`

const Arrow = styled.div`
	${fresponsive(css`
		display: inline-block;
		position: relative;
		height: 89px;
		overflow: clip;
		margin: 0 24px 0 17px;
		width: 180px;

		&::before {
			content: "";
			background: ${colors.blue2};
			position: absolute;
			width: 8px;
			height: 44px;
			top: 0;
			left: 0;
		}

		img {
			display: block;
			position: absolute;
			top: 0;
			right: 0;
			height: 100%;
			max-width: none;
		}
	`)}

	${ftablet(css`
		height: 65px;
		margin: 0 24px 0 10px;
		width: 127px;

		&::before {
			height: 35px;
			width: 6px;
		}
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const LastWord = styled.div`
	color: ${colors.blue2};
	display: inline-block;
`

const Features = styled.div`
	${fresponsive(css`
		display: flex;
		flex-direction: column;
		gap: 110px;
		padding-top: 120px;
	`)}

	${ftablet(css`
		gap: 114px;
		padding-top: 158px;
		align-items: center;
	`)}

  ${fmobile(css`
		gap: 84px;
		padding-top: 71px;
	`)}
`

const Bullets = styled.div`
	${fresponsive(css`
		color: ${colors.charcoal};
		${textStyles.kicker1};
		display: flex;
		gap: 50px;
		padding-left: 12px;
		margin-top: 30px;
	`)}

	${ftablet(css`
		margin-top: 18px;
		justify-content: flex-end;
		padding-left: unset;
		padding-right: 70px;
	`)}

	${fmobile(css`
		display: none;
	`)}
`

const BulletArrow = styled(ArrowSVG)`
	${fresponsive(css`
		width: 20px;
		height: 13px;
	`)}
`

const Bullet = styled.div`
	${fresponsive(css`
		display: flex;
		gap: 12px;
		align-items: center;
	`)}
`

const StyledKicker = styled(Kicker)`
	${fmobile(css`
		justify-content: flex-start;
		margin-left: 20px;
	`)}
`
